import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { actionLabel, changesOf, valueLabel } from '../audit';
import { api, ApiError, type CatalogCategory, type CurrentUser, type RequestDetail as Detail } from '../api';
import { Alert, Badge, Button, Card, ConfirmDialog, EmptyState, Flash, PageHeader, Spinner } from '../components/ui';
import { NudgeButton } from '../components/NudgeButton';
import { IconClipboard } from '../components/icons';
import { ApprovalActions } from '../components/ApprovalActions';
import { ImplementationPanel } from '../components/ImplementationPanel';
import { REQUEST_STATUS } from './MyRequests';
import { canWorkItQueue, isAdmin, isApprovedViewer, isApprover, isSuperAdmin } from '../roles';

const SECTIONS: { category: CatalogCategory; title: string }[] = [
  { category: 'HARDWARE', title: 'Hardware' },
  { category: 'SYSTEM', title: 'Software & systems' },
  { category: 'REMOTE_ACCESS', title: 'Remote access' },
  { category: 'SHAREPOINT_SITE', title: 'SharePoint sites' },
  { category: 'FILESHARE_FOLDER', title: 'Fileshare folders' },
  { category: 'SHARED_MAILBOX', title: 'Shared mailboxes' },
  { category: 'PRINTER', title: 'Printer access' },
];

const LOCATION_LABEL: Record<string, string> = { ONSITE: 'Onsite', OFFICE: 'Office', OTHER: 'Other' };
const EMPLOYMENT_TYPE_LABEL: Record<string, string> = { FIXED_TERM: 'Fixed term', PERMANENT: 'Permanent' };

export function RequestDetailPage({ user }: { user: CurrentUser }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const loaded = useQuery({ queryKey: ['request', id], queryFn: () => api.getRequest(id!), enabled: Boolean(id) });

  // Every hook has to run on every render, including the loading and error ones
  // that return early below. Declaring this further down, after those returns,
  // is what blanked this page: React counts hooks per render and throws when the
  // count changes.
  const [discarding, setDiscarding] = useState(false);
  const [destroying, setDestroying] = useState(false);
  const [reason, setReason] = useState('');
  const [typedReference, setTypedReference] = useState('');
  const [destroyError, setDestroyError] = useState<string | null>(null);

  // Push to onsite tech: a note, then an email, then a tick to wait for.
  const [pushing, setPushing] = useState(false);
  const [onsiteNote, setOnsiteNote] = useState('');
  const [onsiteError, setOnsiteError] = useState<string | null>(null);
  const [onsiteFlash, setOnsiteFlash] = useState<string | null>(null);

  const push = useMutation({
    mutationFn: () => api.pushOnsite(id!, onsiteNote.trim()),
    onSuccess: ({ to }) => {
      setPushing(false);
      setOnsiteNote('');
      setOnsiteFlash(`Sent to ${to}. The request cannot be signed off until the onsite work is ticked done.`);
      void queryClient.invalidateQueries({ queryKey: ['request', id] });
      void queryClient.invalidateQueries({ queryKey: ['request-activity', id] });
    },
    onError: (err) => setOnsiteError(err instanceof ApiError ? err.message : 'Could not send that.'),
  });

  const tickOnsite = useMutation({
    mutationFn: () => api.onsiteDone(id!),
    onSuccess: () => {
      setOnsiteFlash('Onsite work ticked done.');
      void queryClient.invalidateQueries({ queryKey: ['request', id] });
      void queryClient.invalidateQueries({ queryKey: ['request-activity', id] });
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.cancelRequest(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['requests'] });
      void queryClient.invalidateQueries({ queryKey: ['request', id] });
    },
  });

  const clone = useMutation({
    mutationFn: () => api.cloneRequest(id!),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['requests'] });
      navigate(`/requests/${data.request.id}/edit`);
    },
  });

  const destroy = useMutation({
    mutationFn: () => api.deleteRequest(id!, { reason: reason.trim(), confirmReference: typedReference.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['requests'] });
      void queryClient.invalidateQueries({ queryKey: ['archive'] });
      // Nowhere to go back to: the record this page was showing no longer exists.
      navigate('/archive', { replace: true });
    },
    onError: (err) => setDestroyError(err instanceof ApiError ? err.message : 'Could not delete that record.'),
  });

  // The viewer came from the approved list and goes back there; everyone
  // else has a "My requests" to return to.
  const viewer = isApprovedViewer(user.role);
  const backTo = viewer ? '/' : '/requests';
  const backLabel = viewer ? 'Back to approved requests' : 'Back to my requests';

  if (loaded.isPending) return <Spinner label="Loading the request" />;
  if (loaded.isError) {
    const notFound = loaded.error instanceof ApiError && loaded.error.status === 404;
    return (
      <EmptyState
        icon={<IconClipboard />}
        tone={notFound ? 'neutral' : 'danger'}
        title={notFound ? 'That request is not here' : 'Could not load the request'}
        body={
          notFound
            ? 'It may have been cancelled, or it belongs to someone else: drafts are private to the person who raised them.'
            : 'Something went wrong loading it. Try again, and tell IT if it keeps happening.'
        }
        action={<Button onClick={() => navigate(backTo)}>{backLabel}</Button>}
      />
    );
  }

  const { request, canEdit } = loaded.data;
  const status = REQUEST_STATUS[request.status];
  // Matches what the API actually permits. It allowed withdrawing a submitted
  // request all along; the screen just never offered it, so somebody who
  // realised their mistake after signing had no way to take it back.
  const cancellable =
    ['DRAFT', 'PENDING_APPROVAL', 'CHANGES_REQUESTED'].includes(request.status) &&
    (request.requestedBy.id === user.id || isAdmin(user.role));
  const canDecide =
    request.status === 'PENDING_APPROVAL' && (isApprover(user.role));
  const hasDocument = request.signatures.some((signature) => !signature.voidedAt);
  const canImplement =
    isAdmin(user.role) && (request.status === 'APPROVED' || request.status === 'IN_PROGRESS');

  // Chasing is for the person waiting on the outcome, and for IT.
  const stalledOn =
    request.status === 'PENDING_APPROVAL'
      ? ('approval' as const)
      : request.status === 'APPROVED' || request.status === 'IN_PROGRESS'
        ? ('IT' as const)
        : null;
  const canNudge = stalledOn !== null && (request.requestedBy.id === user.id || isAdmin(user.role));
  // The only irreversible action on this page, and the only one an ordinary
  // administrator does not get. Cancelling leaves the record standing, which is
  // right nearly always; this is for a record that should never have existed.
  const canDestroy = isSuperAdmin(user.role);

  // Approvers and the IT side can send it to the onsite tech; only the IT side ticks it done.
  const canPush =
    (isApprover(user.role) || canWorkItQueue(user.role)) &&
    ['PENDING_APPROVAL', 'CHANGES_REQUESTED', 'APPROVED', 'IN_PROGRESS'].includes(request.status);
  const canTickOnsite = canWorkItQueue(user.role);

  return (
    <div>
      <PageHeader
        title={request.reference}
        description={
          <>
            {request.type === 'ENROLMENT' ? 'Enrolment' : 'Offboarding'} · raised by {request.requestedBy.displayName} ·
            last updated {new Date(request.updatedAt).toLocaleString()}
          </>
        }
        actions={
          <>
            <Badge tone={status.tone} dot>
              {status.label}
            </Badge>
            {canEdit && request.requestedBy.id === user.id && (
              <Button onClick={() => navigate(`/requests/${request.id}/edit`)}>Continue editing</Button>
            )}
            {hasDocument && (
              <>
                <Button variant="secondary" onClick={() => window.open(api.pdfUrl(request.id), '_blank')}>
                  Download PDF
                </Button>
                <Button variant="ghost" onClick={() => window.open(api.documentUrl(request.id), '_blank')}>
                  View record
                </Button>
              </>
            )}
            {canNudge && stalledOn && <NudgeButton requestId={request.id} awaiting={stalledOn} />}
            {canPush && (
              <Button
                variant="secondary"
                onClick={() => {
                  setOnsiteNote('');
                  setOnsiteError(null);
                  setPushing(true);
                }}
              >
                {request.onsite ? 'Push to onsite tech again' : 'Push to onsite tech'}
              </Button>
            )}
            {request.status !== 'DRAFT' && !viewer && (
              <Button variant="secondary" onClick={() => clone.mutate()} disabled={clone.isPending}>
                Clone as new {request.type === 'ENROLMENT' ? 'enrolment' : 'offboarding'}
              </Button>
            )}
            {cancellable && (
              <Button variant="secondary" onClick={() => setDiscarding(true)} disabled={cancel.isPending}>
                {request.status === 'DRAFT' ? 'Discard' : 'Cancel request'}
              </Button>
            )}
            {canDestroy && (
              <Button
                variant="ghost"
                className="text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                onClick={() => {
                  setDestroyError(null);
                  setReason('');
                  setTypedReference('');
                  setDestroying(true);
                }}
              >
                Delete permanently
              </Button>
            )}
          </>
        }
      />

      {discarding && (
        <ConfirmDialog
          title={request.status === 'DRAFT' ? `Discard ${request.reference}?` : `Cancel ${request.reference}?`}
          confirmLabel={request.status === 'DRAFT' ? 'Discard' : 'Cancel request'}
          busy={cancel.isPending}
          onCancel={() => setDiscarding(false)}
          onConfirm={() => cancel.mutate()}
          body={
            request.status === 'DRAFT' ? (
              <>
                <p>
                  Everything entered on this draft goes with it: {request.employee?.fullName || 'the employee'}
                  's details and everything selected so far.
                </p>
                <p className="mt-2">Nothing has been submitted, so nobody else has seen it.</p>
              </>
            ) : (
              <>
                <p>
                  This has already been signed and sent{request.status === 'PENDING_APPROVAL' ? ' to the approvers' : ''}.
                  Cancelling stops the work; the signed record stays as proof of what was asked for and withdrawn.
                </p>
                <p className="mt-2">Anyone waiting on it is told.</p>
              </>
            )
          }
        />
      )}

      {destroying && (
        <ConfirmDialog
          title={`Delete ${request.reference} for good?`}
          confirmLabel="Delete permanently"
          busy={destroy.isPending}
          confirmDisabled={
            reason.trim().length < 10 || typedReference.trim().toUpperCase() !== request.reference.toUpperCase()
          }
          onCancel={() => setDestroying(false)}
          onConfirm={() => {
            setDestroyError(null);
            destroy.mutate();
          }}
          body={
            <>
              <p>
                This removes the record itself: {request.employee?.fullName || 'the employee'}
                &rsquo;s details, everything requested
                {hasDocument ? ', and the signed document with the signatures on it' : ''}. It cannot be
                undone, and no backup inside this application holds a copy.
              </p>
              <p className="mt-2">
                If you only want to stop the work, <strong>cancel</strong> it instead: that leaves the record
                standing as proof of what was asked for and withdrawn.
              </p>

              <label className="mt-4 block">
                <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Why is it being deleted?
                </span>
                <textarea
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  rows={2}
                  placeholder="Raised against the wrong person"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
                {/* The ten-character minimum is deliberate: "test" is not a
                    reason. But a button that silently refuses to click with
                    no explanation just reads as broken. Say why. */}
                {reason.trim().length > 0 && reason.trim().length < 10 && (
                  <span className="mt-1 block text-[11px] text-copper-700 dark:text-copper-400">
                    {10 - reason.trim().length} more character{10 - reason.trim().length === 1 ? '' : 's'} needed.
                  </span>
                )}
                <span className="mt-1 block text-[11px] text-slate-500 dark:text-slate-400">
                  Kept in the audit log, which survives the deletion. It is the only account of this record
                  that will remain.
                </span>
              </label>

              <label className="mt-3 block">
                <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Type <span className="font-mono">{request.reference}</span> to confirm
                </span>
                <input
                  value={typedReference}
                  onChange={(event) => setTypedReference(event.target.value)}
                  placeholder={request.reference}
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </label>

              {destroyError && (
                <p className="mt-3 text-xs text-red-700 dark:text-red-400" role="alert">
                  {destroyError}
                </p>
              )}
            </>
          }
        />
      )}

      {cancel.isError && (
        <div className="mb-4">
          <Alert>{cancel.error instanceof ApiError ? cancel.error.message : 'Could not cancel it.'}</Alert>
        </div>
      )}

      {clone.isError && (
        <div className="mb-4">
          <Alert>{clone.error instanceof ApiError ? clone.error.message : 'Could not clone it.'}</Alert>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <Employee request={request} />
        </Card>

        <Card className="p-6">
          <h2 className="text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
            Progress
          </h2>
          <Timeline request={request} />
        </Card>
      </div>

      <div className="mt-6 grid gap-6 sm:grid-cols-2">
        {SECTIONS.map((section) => {
          const items = request.items.filter((item) => item.category === section.category);
          return (
            <Card key={section.category} className="p-6">
              <div className="mb-3 flex items-center gap-3">
                <h2 className="text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
                  {section.title}
                </h2>
                <Badge tone="neutral">{items.length}</Badge>
              </div>

              {items.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">None requested.</p>
              ) : (
                <ul className="space-y-1.5 text-sm">
                  {items
                    .slice()
                    .sort((a, b) => (a.legacyNumber ?? 0) - (b.legacyNumber ?? 0))
                    .map((item) => (
                      <li key={item.catalogItemId} className="flex flex-wrap items-baseline gap-x-2">
                        {item.legacyNumber !== null && (
                          <span className="text-xs tabular-nums text-slate-500 dark:text-slate-400">{item.legacyNumber}</span>
                        )}
                        <span className="text-slate-800 dark:text-slate-200">{item.name}</span>
                        {item.isCompulsory && <Badge tone="info">Everyone gets this</Badge>}
                        {item.accessLevel && (
                          <Badge tone={item.accessLevel === 'RW' ? 'copper' : 'neutral'}>
                            {item.accessLevel === 'RW' ? 'Read & write' : 'Read only'}
                          </Badge>
                        )}
                        {(item.assetDetails || item.notes) && (
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            ({item.assetDetails ?? item.notes})
                          </span>
                        )}
                      </li>
                    ))}
                </ul>
              )}
            </Card>
          );
        })}
      </div>

      {onsiteFlash && (
        <div className="mb-6">
          <Flash message={onsiteFlash} onDismiss={() => setOnsiteFlash(null)} />
        </div>
      )}

      {request.onsite && (
        <Card
          className={`mb-6 p-5 ${
            request.onsite.doneAt ? '' : 'border-copper-300 dark:border-copper-800'
          }`}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
                Onsite tech
              </h2>
              <p className="mt-2 text-sm text-slate-800 dark:text-slate-200">
                Pushed to the onsite tech by {request.onsite.pushedBy ?? 'a deleted account'} on{' '}
                {new Date(request.onsite.pushedAt).toLocaleString()}.
              </p>
              {request.onsite.note && (
                <p className="mt-2 border-l-2 border-copper-300 pl-3 text-sm whitespace-pre-wrap text-slate-700 dark:border-copper-700 dark:text-slate-300">
                  {request.onsite.note}
                </p>
              )}
              <p className="mt-3 text-sm">
                {request.onsite.doneAt ? (
                  <Badge tone="success" dot>
                    Onsite work done by {request.onsite.doneBy ?? 'a deleted account'} on{' '}
                    {new Date(request.onsite.doneAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                  </Badge>
                ) : (
                  <Badge tone="warning" dot>
                    Onsite work outstanding: cannot be signed off until this is ticked
                  </Badge>
                )}
              </p>
            </div>
            {canTickOnsite && !request.onsite.doneAt && (
              <Button onClick={() => tickOnsite.mutate()} disabled={tickOnsite.isPending}>
                {tickOnsite.isPending ? 'Ticking…' : 'Tick onsite work done'}
              </Button>
            )}
          </div>
        </Card>
      )}

      {pushing && (
        <ConfirmDialog
          title={request.onsite ? 'Push to the onsite tech again?' : 'Push to the onsite tech?'}
          confirmLabel="Send to onsite tech"
          tone="primary"
          busy={push.isPending}
          onCancel={() => setPushing(false)}
          onConfirm={() => {
            setOnsiteError(null);
            push.mutate();
          }}
          body={
            <>
              <p>
                This emails the whole request, with your note, to the onsite tech mailbox. From then on the
                request shows as waiting on the onsite tech, and it cannot be signed off until somebody on the IT
                side ticks the onsite work done.
              </p>
              <label className="mt-4 block">
                <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Note for the onsite tech (optional)
                </span>
                <textarea
                  value={onsiteNote}
                  onChange={(event) => setOnsiteNote(event.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="What needs doing on site, where, and by when"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </label>
              {onsiteError && (
                <div className="mt-3">
                  <Alert>{onsiteError}</Alert>
                </div>
              )}
            </>
          }
        />
      )}

      {canDecide && <ApprovalActions request={request} user={user} />}

      {canImplement && <ImplementationPanel request={request} user={user} />}

      {request.status === 'COMPLETED' && request.implementation && (
        <Card className="mt-6 p-6">
          <h2 className="mb-4 text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
            IT implementation
          </h2>
          <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
            <Field label="Technician" value={request.implementation.technicianName} />
            <Field label="Email address created" value={request.implementation.createdEmailAddress} />
            <Field
              label="Completed"
              value={
                request.implementation.completedDate
                  ? new Date(request.implementation.completedDate).toLocaleString()
                  : null
              }
            />
            <Field label="Completed by" value={request.admin?.displayName ?? null} />
          </dl>
          {request.implementation.implementationNotes && (
            <p className="mt-5 border-t border-slate-200 pt-4 text-sm whitespace-pre-wrap text-slate-800 dark:border-slate-800 dark:text-slate-200">
              {request.implementation.implementationNotes}
            </p>
          )}
        </Card>
      )}

      {request.approvalComments.length > 0 && (
        <Card className="mt-6 p-6">
          <h2 className="mb-3 text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
            Approver comments
          </h2>
          <ul className="space-y-3">
            {request.approvalComments.map((note, index) => (
              <li key={index} className="text-sm">
                <p className="whitespace-pre-wrap text-slate-800 dark:text-slate-200">{note.body}</p>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {new Date(note.createdAt).toLocaleString()}
                </p>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {request.signatures.length > 0 && <Signatures request={request} />}

      {request.specialInstructions && (
        <Card className="mt-6 p-6">
          <h2 className="mb-3 text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
            Special instructions
          </h2>
          <p className="text-sm whitespace-pre-wrap text-slate-800 dark:text-slate-200">
            {request.specialInstructions}
          </p>
        </Card>
      )}

      {request.approver && (isApprover(user.role) || canWorkItQueue(user.role)) && (
        <Messages request={request} user={user} />
      )}

      <Activity requestId={request.id} />

      <p className="mt-8 text-sm">
        <Link to={backTo} className="text-brand-700 underline underline-offset-4 dark:text-brand-300">
          {backLabel}
        </Link>
      </p>
    </div>
  );
}

/**
 * This request's own history, from the audit trail.
 *
 * The Progress card beside the employee details is derived from the request's
 * status fields, so it can only ever show the happy path: it cannot show that
 * something was returned twice, or that three reminders went out on a Friday.
 * The trail can, because it is what actually happened rather than what the
 * current state implies.
 *
 * Loaded separately from the request. It is the least urgent thing on the page
 * and the only part that grows without limit, so it must not hold up the record
 * itself, and a failure to load it must not blank the page.
 */
/**
 * A follow-up thread between the approver and IT, for the one thing the audit
 * trail and the approval comments do not cover: a question that goes the
 * other way, from IT back to whoever signed off. Not shown to the requester,
 * and only once there is a specific approver to have the conversation with.
 * Each message emails the other side, the same as everything else this app
 * sends mail for.
 */
function Messages({ request, user }: { request: Detail; user: CurrentUser }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');

  const loaded = useQuery({
    queryKey: ['request-messages', request.id],
    queryFn: () => api.requestMessages(request.id),
  });

  const send = useMutation({
    mutationFn: () => api.sendRequestMessage(request.id, body),
    onSuccess: () => {
      setBody('');
      void queryClient.invalidateQueries({ queryKey: ['request-messages', request.id] });
    },
  });

  return (
    <Card className="mt-6 p-6">
      <h2 className="text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
        Between the approver and IT
      </h2>
      <p className="mt-1 mb-4 text-xs text-slate-500 dark:text-slate-400">
        Not part of {request.requestedBy.displayName}&rsquo;s view of this request. Each message emails
        whoever is on the other side.
      </p>

      {loaded.isPending ? (
        <Spinner label="Loading the conversation" />
      ) : loaded.isError ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">Could not load the conversation.</p>
      ) : loaded.data.messages.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Nothing yet. Ask a question here rather than by chasing them another way, and it is on the
          record for both sides.
        </p>
      ) : (
        <ul className="space-y-3">
          {loaded.data.messages.map((message) => (
            <li
              key={message.id}
              className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                message.mine
                  ? 'ml-auto bg-brand-700 text-white'
                  : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200'
              }`}
            >
              <p className="whitespace-pre-wrap">{message.body}</p>
              <p
                className={`mt-1 text-[11px] ${message.mine ? 'text-brand-100' : 'text-slate-500 dark:text-slate-400'}`}
              >
                {message.authorName} · {new Date(message.createdAt).toLocaleString()}
              </p>
            </li>
          ))}
        </ul>
      )}

      <form
        className="mt-4 flex items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (body.trim().length > 0 && !send.isPending) send.mutate();
        }}
      >
        <textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          rows={2}
          placeholder="Ask a follow-up question"
          className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
        />
        <Button type="submit" disabled={send.isPending || body.trim().length === 0}>
          {send.isPending ? 'Sending…' : 'Send'}
        </Button>
      </form>
      {send.isError && (
        <p className="mt-2 text-xs text-copper-700 dark:text-copper-400">
          {send.error instanceof ApiError ? send.error.message : 'Could not send that.'}
        </p>
      )}
    </Card>
  );
}

function Activity({ requestId }: { requestId: string }) {
  const loaded = useQuery({
    queryKey: ['request-activity', requestId],
    queryFn: () => api.requestActivity(requestId),
  });

  if (loaded.isPending || loaded.isError || loaded.data.events.length === 0) return null;

  // Newest first: on a request that has been round the houses, the last thing
  // that happened is the thing somebody opened the page to find.
  const events = [...loaded.data.events].reverse();

  return (
    <Card className="mt-6 p-6">
      <h2 className="mb-4 text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
        Activity
      </h2>
      <ol className="space-y-3">
        {events.map((event) => {
          const changes = changesOf(event);
          return (
            <li key={event.id} className="flex gap-3 text-sm">
              <span
                aria-hidden="true"
                className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300 dark:bg-slate-600"
              />
              <div className="min-w-0">
                <span className="text-slate-900 dark:text-slate-100">{actionLabel(event.action)}</span>
                {event.actor && (
                  <span className="text-slate-600 dark:text-slate-400"> by {event.actor.name}</span>
                )}
                <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {new Date(event.createdAt).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                  {changes
                    .filter((change) => change.field === 'status')
                    .map((change) => (
                      <span key={change.field}>
                        {' · '}
                        {valueLabel(change.from)} → {valueLabel(change.to)}
                      </span>
                    ))}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

const STAGE_LABEL: Record<string, string> = {
  REQUEST: 'Requested by',
  APPROVAL: 'Approved by',
  IMPLEMENTATION: 'Implemented by',
};

/** Attestations, not images. The marks live only in the signed record. */
function Signatures({ request }: { request: Detail }) {
  return (
    <Card className="mt-6 p-6">
      <h2 className="mb-4 text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
        Signatures
      </h2>
      <ul className="space-y-4">
        {request.signatures.map((signature, index) => (
          <li key={index} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
            <span className="text-slate-500 dark:text-slate-400">{STAGE_LABEL[signature.stage] ?? signature.stage}</span>
            <span className="font-medium text-slate-900 dark:text-slate-100">{signature.signerName}</span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {new Date(signature.signedAt).toLocaleString()}
            </span>
            {signature.voidedAt ? (
              <Badge tone="neutral">Voided, returned for changes</Badge>
            ) : (
              <Badge tone="success">Valid</Badge>
            )}
            <code className="w-full font-mono text-[11px] break-all text-slate-500 dark:text-slate-400">
              {signature.payloadSha256}
            </code>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
        Each hash covers the request exactly as it stood when signed. The signature marks themselves live only
        inside the signed record, never in the database.
      </p>
    </Card>
  );
}

function Employee({ request }: { request: Detail }) {
  const e = request.employee;
  if (!e) return <p className="text-sm text-slate-500">No employee details yet.</p>;

  return (
    <>
      <h2 className="mb-4 text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
        Employee
      </h2>
      <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
        <Field label="Full name" value={e.fullName} />
        <Field label="ID / passport" value={e.idPassport} />
        <Field label="Job title" value={e.jobTitle} />
        <Field label="Department" value={e.departmentName} />
        <Field label="Manager" value={e.managerName} />
        <Field
          label="Start date"
          value={e.startDate ? new Date(e.startDate).toLocaleDateString(undefined, { dateStyle: 'long' }) : null}
        />
        {e.employmentType && (
          <>
            <Field label="Employment type" value={EMPLOYMENT_TYPE_LABEL[e.employmentType] ?? e.employmentType} />
            {e.employmentType === 'FIXED_TERM' && (
              <>
                <Field
                  label="Contract end date"
                  value={
                    e.contractEndDate ? new Date(e.contractEndDate).toLocaleDateString(undefined, { dateStyle: 'long' }) : null
                  }
                />
                <Field
                  label="Taking over an existing account"
                  value={e.takingOverExistingAccount ? (e.takeoverAccountDetails ?? 'Yes') : 'No'}
                />
              </>
            )}
          </>
        )}
        <Field label="Contact number" value={e.contactNumber} />
        <Field
          label="Location"
          value={
            e.locationType
              ? e.locationType === 'OTHER'
                ? `Other: ${e.locationOther ?? 'not specified'}`
                : (LOCATION_LABEL[e.locationType] ?? e.locationType)
              : null
          }
        />
        <Field
          label="Email"
          value={e.emailRequired ? (e.emailAddress || 'Required (IT to assign)') : 'Not required'}
        />
      </dl>
    </>
  );
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className={value ? 'text-sm text-slate-900 dark:text-slate-100' : 'text-sm text-slate-500 dark:text-slate-400'}>
        {value || 'Not given'}
      </dd>
    </div>
  );
}

function Timeline({ request }: { request: Detail }) {
  const steps = [
    { label: 'Created', at: request.createdAt, done: true },
    { label: 'Submitted for approval', at: request.submittedAt, done: Boolean(request.submittedAt) },
    { label: 'Approved', at: request.approvedAt, done: ['APPROVED', 'IN_PROGRESS', 'COMPLETED'].includes(request.status) },
    { label: 'With IT', at: null, done: ['IN_PROGRESS', 'COMPLETED'].includes(request.status) },
    { label: 'Implemented and signed off', at: request.completedAt, done: request.status === 'COMPLETED' },
  ];

  return (
    <ol className="mt-4 space-y-4">
      {steps.map((step) => (
        <li key={step.label} className="flex gap-3">
          <span
            className={
              step.done
                ? 'mt-1 h-2 w-2 shrink-0 rounded-full bg-brand-600 dark:bg-brand-400'
                : 'mt-1 h-2 w-2 shrink-0 rounded-full bg-slate-300 dark:bg-slate-700'
            }
            aria-hidden="true"
          />
          <div>
            <p
              className={
                step.done
                  ? 'text-sm text-slate-900 dark:text-slate-100'
                  : 'text-sm text-slate-500 dark:text-slate-400'
              }
            >
              {step.label}
            </p>
            {step.at && (
              <p className="text-xs text-slate-500 dark:text-slate-400">{new Date(step.at).toLocaleString()}</p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}
